import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipeDynamicCaption from 'photoswipe-dynamic-caption-plugin';
import ObjectPosition from '@vovayatsyuk/photoswipe-object-position';
import './vendor.css';
import './styles.css';
import { ZoomIn, ZoomOut, X } from 'lucide';
import { loadSvg } from '../utils';
import { readCssNumber } from '../../core/motion';
import Swiper from 'swiper';

const GALLERY_SELECTOR = '[data-gallery]';
const BOUND_KEY = 'true';

let initialized = false;


type IconNode = [string, Record<string, string>][];

function isFullyInViewport(el: Element): boolean {
    const rect = el.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    return rect.top >= 0 && rect.left >= 0 &&
        rect.bottom <= vh && rect.right <= vw;
}

function lucideToSvg(icon: IconNode): string {
    const children = icon.map(([tag, attrs]) => {
        const attrStr = Object.entries(attrs).map(([k, v]) => `${k}="${v}"`).join(' ');
        return `<${tag} ${attrStr}/>`;
    }).join('');
    return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${children}</svg>`;
}

const OVERLAP_FADE_ATTR = 'data-pswp-overlap-fade';

function getElementsOverlapping(img: HTMLImageElement): HTMLElement[] {
    const rect = img.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return [];
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const points: [number, number][] = [
        [cx, cy],
        [rect.left + 2, rect.top + 2],
        [rect.right - 2, rect.top + 2],
        [rect.left + 2, rect.bottom - 2],
        [rect.right - 2, rect.bottom - 2],
        [rect.left + 2, cy],   // 中左（swiper nav-prev）
        [rect.right - 2, cy],  // 中右（swiper nav-next）
    ];
    const result = new Set<HTMLElement>();
    for (const [x, y] of points) {
        const stack = document.elementsFromPoint(x, y);
        const imgIdx = stack.indexOf(img);
        if (imgIdx <= 0) continue;
        for (let i = 0; i < imgIdx; i++) {
            const el = stack[i];
            if (el instanceof HTMLElement && !el.contains(img)) {
                result.add(el);
            }
        }
    }
    // 他の候補の子孫となる要素を除外（祖先のみフェードすれば十分）
    const candidates = [...result];
    return candidates.filter(el => !candidates.some(other => other !== el && other.contains(el)));
}


function restoreOverlappingElements(): void {
    document.querySelectorAll<HTMLElement>(`[${OVERLAP_FADE_ATTR}]`).forEach(el => {
        el.removeAttribute(OVERLAP_FADE_ATTR);
        el.style.transition = 'opacity 0.2s ease';
        el.style.opacity = '';
        el.style.pointerEvents = '';
        el.addEventListener('transitionend', () => { el.style.transition = ''; }, { once: true });
    });
}

export function initPhotoSwipe(): void {
    if (initialized) return;
    initialized = true;
    document.addEventListener('ui:update', () => mount(document));
}

async function mount(scope: ParentNode): Promise<void> {
    const galleries = Array.from(scope.querySelectorAll<HTMLElement>(GALLERY_SELECTOR));

    for (const gallery of galleries) {
        if (gallery.dataset.pswpBound === BOUND_KEY) continue;
        gallery.dataset.pswpBound = BOUND_KEY;

        const [prevSvg, nextSvg] = await Promise.all([
            loadSvg('./svg/chevron-left.svg'),
            loadSvg('./svg/chevron-right.svg'),
        ]);

        const links = Array.from(gallery.querySelectorAll<HTMLAnchorElement>('a'));
        const hasMultiple = links.length > 1;
        // pswp オープン前に計算した「スライドごとの重なり要素」キャッシュ
        const overlapCache: HTMLElement[][] = [];

        // data-pswp-width/height 未指定の href 画像サイズを事前取得
        const hrefSizeMap = new Map<string, { w: number; h: number }>();
        links.forEach((link) => {
            if (link.dataset.pswpWidth && link.dataset.pswpHeight) return;
            const img = new Image();
            img.onload = () => { hrefSizeMap.set(link.href, { w: img.naturalWidth, h: img.naturalHeight }); };
            img.src = link.href;
        });

        const lightbox = new PhotoSwipeLightbox({
            gallery: gallery,
            children: 'a',
            bgOpacity: 1,
            pswpModule: () => import('photoswipe'),
            paddingFn: () => {
                const top = readCssNumber(document.documentElement, '--pswp-padding-top', 20);
                const bottom = hasMultiple
                    ? readCssNumber(document.documentElement, '--pswp-thumb-size', 72)
                    + readCssNumber(document.documentElement, '--pswp-thumb-strip-pt', 8)
                    + readCssNumber(document.documentElement, '--pswp-thumb-strip-pb', 16)
                    : top;
                return { top, bottom, left: 0, right: 0 };
            },
            arrowPrevSVG: prevSvg || undefined,
            arrowNextSVG: nextSvg || undefined,
            zoomSVG: lucideToSvg(ZoomIn as IconNode),
            closeSVG: lucideToSvg(X as IconNode),
        });

        // thumbCropped: true で innerRect を生成、ObjectPosition で object-position に応じた位置を調整
        // data-pswp-width/height 指定時はそのまま使用、未指定時は href 画像のプリロードサイズを使用
        // ObjectPosition プラグインは itemData.width/height を参照するため w/h と両方を更新する
        lightbox.addFilter('itemData', (itemData) => {
            if (!itemData.w || !itemData.h) {
                // プリロード済みの href 画像サイズを使用
                const cached = hrefSizeMap.get(itemData.src ?? '');
                if (cached) {
                    itemData.w = cached.w;
                    itemData.h = cached.h;
                    itemData.width = cached.w;
                    itemData.height = cached.h;
                } else {
                    // フォールバック: サムネのサイズ（プリロード未完了時）
                    const img = itemData.element?.querySelector<HTMLImageElement>('img');
                    if (img) {
                        const w = img.naturalWidth || parseInt(img.getAttribute('width') ?? '0') || itemData.w;
                        const h = img.naturalHeight || parseInt(img.getAttribute('height') ?? '0') || itemData.h;
                        itemData.w = w;
                        itemData.h = h;
                        itemData.width = w;
                        itemData.height = h;
                    }
                }
            }
            return { ...itemData, thumbCropped: true };
        });

        // プリロード未完了のフォールバック: スライド画像ロード後にサイズ補正
        lightbox.on('uiRegister', () => {
            const pswp = lightbox.pswp!;
            const updateSlideSize = () => {
                const slide = pswp.currSlide;
                if (!slide?.content) return;
                const a = slide.content.data.element as HTMLAnchorElement | undefined;
                if (a?.dataset?.pswpWidth && a?.dataset?.pswpHeight) return;
                const img = slide.content.element as HTMLImageElement;
                if (!(img instanceof HTMLImageElement) || !img.naturalWidth) return;
                if (slide.content.data.w === img.naturalWidth) return;
                slide.content.data.w = img.naturalWidth;
                slide.content.data.h = img.naturalHeight;
                slide.content.data.width = img.naturalWidth;
                slide.content.data.height = img.naturalHeight;
                slide.updateContentSize(true);
            };
            pswp.on('change', () => {
                const img = pswp.currSlide?.content?.element;
                if (img instanceof HTMLImageElement) {
                    if (img.complete && img.naturalWidth) updateSlideSize();
                    else img.addEventListener('load', updateSlideSize, { once: true });
                }
            });
            pswp.on('openingAnimationEnd', updateSlideSize);
        });
        new ObjectPosition(lightbox);

        new PhotoSwipeDynamicCaption(lightbox, {
            // mobileLayoutBreakpoint: 700,
            type: 'auto',
            mobileCaptionOverlapRatio: 1,
            captionContent: '.pswp-caption-content'
        });

        // ビューポート内 → zoom、画面外 → fade
        // クリック時点 (pswp 生成前) に lightbox.options に設定することで pswp.options に正しくコピーされる
        gallery.addEventListener('click', (e) => {
            const a = (e.target as Element).closest<HTMLAnchorElement>('a');
            if (!a) return;
            const img = a.querySelector<HTMLImageElement>('img');
            lightbox.options.showHideAnimationType = (img && isFullyInViewport(img)) ? 'zoom' : 'fade';
            // pswp オーバーレイ表示前に全スライドの重なり要素を計算してキャッシュ
            links.forEach((link, i) => {
                const linkImg = link.querySelector<HTMLImageElement>('img');
                overlapCache[i] = linkImg ? getElementsOverlapping(linkImg) : [];
            });
            // キャッシュを再利用してクリックスライドをフェード
            const clickedIdx = links.indexOf(a);
            for (const el of (clickedIdx !== -1 ? overlapCache[clickedIdx] : [])) {
                el.setAttribute(OVERLAP_FADE_ATTR, '');
                el.style.transition = 'opacity 0.12s ease';
                el.style.opacity = '0';
                el.style.pointerEvents = 'none';
            }
        }, true);

        lightbox.on('destroy', () => {
            restoreOverlappingElements();
        });

        // 閉じる時もビューポート確認、zoom 時はキャプションを隠す
        lightbox.on('close', () => {
            const pswp = lightbox.pswp;
            if (!pswp) return;
            const img = links[pswp.currIndex]?.querySelector<HTMLImageElement>('img');
            const isZoom = !!(img && isFullyInViewport(img));
            pswp.options.showHideAnimationType = isZoom ? 'zoom' : 'fade';
            if (isZoom) pswp.element?.classList.add('pswp--zoom-anim');
        });

        lightbox.on('uiRegister', () => {
            const pswp = lightbox.pswp!;

            // zoom ボタン: ズーム不可は disable、ズーム中は ZoomOut アイコン
            const zoomInSvg = lucideToSvg(ZoomIn as IconNode);
            const zoomOutSvg = lucideToSvg(ZoomOut as IconNode);
            const updateZoomBtn = () => {
                const slide = pswp.currSlide;
                const zoomBtn = pswp.element?.querySelector<HTMLButtonElement>('.pswp__button--zoom');
                if (!slide || !zoomBtn) return;
                const canZoom = slide.zoomLevels.secondary > slide.zoomLevels.initial;
                zoomBtn.disabled = !canZoom;
                const isZoomed = canZoom && slide.currZoomLevel > slide.zoomLevels.initial;
                zoomBtn.innerHTML = isZoomed ? zoomOutSvg : zoomInSvg;
            };
            pswp.on('zoomPanUpdate', updateZoomBtn);
            pswp.on('change', updateZoomBtn);
            pswp.on('resize', updateZoomBtn);

            // zoom open 中はキャプションを隠す
            pswp.on('openingAnimationStart', () => {
                if (pswp.options.showHideAnimationType === 'zoom') {
                    pswp.element?.classList.add('pswp--zoom-anim');
                }
            });
            let opened = false;
            pswp.on('openingAnimationEnd', () => {
                pswp.element?.classList.remove('pswp--zoom-anim');
                opened = true;
            });

            // スライド移動時: 前スライドの重なり要素を復元し、新スライドの重なり要素をフェード
            pswp.on('change', () => {
                if (!opened) return;
                const next = overlapCache[pswp.currIndex] ?? [];
                const nextSet = new Set(next);
                document.querySelectorAll<HTMLElement>(`[${OVERLAP_FADE_ATTR}]`).forEach(el => {
                    if (nextSet.has(el)) return;
                    el.removeAttribute(OVERLAP_FADE_ATTR);
                    el.style.transition = 'opacity 0.2s ease';
                    el.style.opacity = '';
                    el.style.pointerEvents = '';
                    el.addEventListener('transitionend', () => { el.style.transition = ''; }, { once: true });
                });
                for (const el of next) {
                    if (!el.hasAttribute(OVERLAP_FADE_ATTR)) {
                        el.setAttribute(OVERLAP_FADE_ATTR, '');
                        el.style.transition = 'opacity 0.12s ease';
                        el.style.opacity = '0';
                        el.style.pointerEvents = 'none';
                    }
                }
            });

            if (!hasMultiple || !pswp.ui) return;

            pswp.ui.registerElement({
                name: 'thumbs',
                order: 9,
                isButton: false,
                appendTo: 'root',
                onInit: (el, pswp) => {
                    const swiperEl = document.createElement('div');
                    swiperEl.className = 'swiper pswp-thumbs-swiper';
                    swiperEl.dataset.swiperBound = 'true';

                    const wrapperEl = document.createElement('div');
                    wrapperEl.className = 'swiper-wrapper';

                    links.forEach((link) => {
                        const img = link.querySelector('img');
                        const slide = document.createElement('div');
                        slide.className = 'swiper-slide';
                        const thumbImg = document.createElement('img');
                        thumbImg.src = img?.src ?? link.href;
                        thumbImg.alt = img?.alt ?? '';
                        thumbImg.loading = 'lazy';
                        slide.appendChild(thumbImg);
                        wrapperEl.appendChild(slide);
                    });

                    swiperEl.appendChild(wrapperEl);
                    el.appendChild(swiperEl);

                    const thumbSwiper = new Swiper(swiperEl, {
                        slidesPerView: 'auto',
                        centeredSlides: true,
                        spaceBetween: readCssNumber(document.documentElement, '--pswp-thumb-gap', 8),
                        initialSlide: pswp.currIndex,
                    });

                    const thumbSlides = Array.from(wrapperEl.querySelectorAll<HTMLElement>('.swiper-slide'));

                    const setActive = (index: number) => {
                        thumbSlides.forEach((s, i) => s.classList.toggle('active', i === index));
                    };

                    setActive(pswp.currIndex);

                    // プログラム的な slideTo 中は slideChange → pswp.goTo() を抑制
                    let suppressSlideChange = false;
                    const slideTo = (index: number, speed = 300) => {
                        suppressSlideChange = true;
                        thumbSwiper.slideTo(index, speed);
                        suppressSlideChange = false;
                    };

                    pswp.on('change', () => {
                        const index = pswp.currIndex;
                        setActive(index);
                        if (thumbSwiper.activeIndex !== index) {
                            slideTo(index);
                        }
                    });

                    // Swiper ドラッグ → PhotoSwipe
                    thumbSwiper.on('slideChange', () => {
                        if (suppressSlideChange) return;
                        const newIndex = thumbSwiper.activeIndex;
                        setActive(newIndex);
                        if (newIndex !== pswp.currIndex) {
                            pswp.goTo(newIndex);
                        }
                    });

                    thumbSlides.forEach((slide, i) => {
                        slide.addEventListener('click', () => pswp.goTo(i));
                    });

                    // サムネイル画像以外（背景部分）クリックで閉じる
                    el.addEventListener('click', (e) => {
                        if (!(e.target as Element).closest('.swiper-slide')) {
                            pswp.close();
                        }
                    });
                    pswp.on('destroy', () => thumbSwiper.destroy(true, true));
                },
            });
        });

        lightbox.init();
    }
}
